import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { push } from '@lagunovsky/redux-react-router'
import { groupBy, isNil, isUndefined, maxBy, orderBy, uniqBy, keys, max } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getLabel } from '../../utils/StoreUtils'
import ProgressBar from '../../components/progress/ProgressBar'
import {
    getLocalizationPicto,
    calculateAverage,
    searchMaxAnalysis,
    filterValid,
    filterResult,
    filterQuantification,
    searchMinAnalysis,
    searchP90Analysis,
    searchP90Value,
    searchMinValue,
    searchMaxValue,
    ResultWithStatus,
} from '../../utils/AnalyseUtils'
import { getDate } from '../../utils/DateUtil'
import { nbPerPageLabel } from '../../referencial/constants/ReferencialConstants'
import SampleModal from '../../quality/components/qualityResearch/SampleModal'
import { exportFile } from '../../utils/ExportDataUtil'
import Card from '../card/Card'
import { getI18nTitleDataLength, normalizeString } from '../../utils/StringUtil'
import { Accordion, Grid, IconButton, Stack, Tooltip } from '@mui/material'
import { RESEARCH_TYPE } from 'quality/constants/QualityConstants'
import useListIndexed from 'utils/customHook/useListIndexed'
import useBoolean from 'utils/customHook/useBoolean'
import { SimpleFilterField } from '../datatable/SimpleSelectionTable'
import { ParameterGraphModal } from 'quality/components/qualityComponents/ParameterGraph'
import { AccordionTitle } from 'components/styled/Accordions'
import { NewTable, WrapperAccordionDetails } from 'components/datatable/NewTable'
import { calculateThresholdsResult, getMarkerColor } from 'utils/QualityThresholdUtils'
import { SHOW_SANDRE_CODE } from 'account/constants/AccountSettingsConstants'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'
import { FileDownload, Info, InsertChart } from '@mui/icons-material'
import { blackColor, whiteColor } from 'utils/constants/ColorTheme'
import { QualityThresholdType } from 'quality/shapes/QualityThresholdShapes'

const EXPORT_ULTRA_LIGHT = ['stationCode', 'stationName', 'date', 'parameterCode', 'parameter', 'unitCode', 'unit', 'result', 'x', 'y', 'projection']
const SORTING_TYPE = {
    MAX: '1',
    MIN: '2',
    AVERAGE: '3',
    P90: '4',
    FIX: '5',
}

const CollapsibleTable = ({
    displayAnalysis = {},
    analysis = [],
    researchType,
    isOnlyOneParameter = false,
    thresholds = [],

    openSampleModal = () => {},
}) => {
    const dispatch = useDispatch()

    const {
        qualitometers,
        parameters,
        units,
        settings,
        contributors,
        qualifications,
        status,
        laboratories,
    } = useSelector(store => ({
        qualitometers: store.QualityReducer.qualitometersLight,
        parameters: store.ParameterReducer.parameters,
        units: store.UnitReducer.units,
        settings: store.AdministrationReducer.settings,
        contributors: store.ContributorReducer.contributors,
        qualifications: store.QualityReducer.qualifications,
        status: store.QualityReducer.status,
        laboratories: store.ContributorReducer.laboratories,
    }), shallowEqual)

    const parametersIndexed = useListIndexed(parameters, 'code')
    const unitsIndexed = useListIndexed(units, 'code')
    const contributorsIndexed = useListIndexed(contributors, 'id')

    const generalShowSandreCode = useApplicationSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : true)
    const showSandreCode = useAccountSetting(SHOW_SANDRE_CODE, setting => setting ? parseInt(setting) : generalShowSandreCode)

    const ultraLightMode = useMemo(() => max(analysis.map(a => keys(a).length)) < 20, [analysis])

    const {
        value: isOpen,
        setFalse: close,
        setTrue: open,
    } = useBoolean(false)

    const formattedAnalysis = useMemo(() => {
        return analysis.map(a => {
            return {
                labo: { value: contributorsIndexed[a.labo]?.labelDisplay ?? '' },
                date: { value: getDate(a.analysisDate) },
                parameter: { value: (showSandreCode ? parametersIndexed[a.parameter]?.labelWithCode : parametersIndexed[a.parameter]?.name) ?? a.parameter },
                statusAndResult: {
                    value: ultraLightMode ? (
                        <Grid container spacing={0.5} alignItems='center' sx={{ width: '100%' }}>
                            <Grid size='grow' sx={{ backgroundColor: a.color }}>
                                { a.value }
                            </Grid>
                        </Grid>
                    ) : (
                        <ResultWithStatus
                            qualification={a.qualification}
                            status={a.status}
                            result={a.value}
                            color={a.color}
                        />
                    ),
                },
                unit: { value: unitsIndexed[a.unit]?.symbol },
                nullValue: { value: getLocalizationPicto(a.localization) },
                id: a.id,
            }
        })
    }, [analysis, contributorsIndexed, parametersIndexed, showSandreCode, unitsIndexed, ultraLightMode])

    const nbParameter = useMemo(() => uniqBy(analysis, 'parameter').length, [analysis])
    const parameter = useMemo(() => isOnlyOneParameter ? parametersIndexed[displayAnalysis.parameter] : {}, [isOnlyOneParameter, parametersIndexed, displayAnalysis])

    const qualitometer = useMemo(() => qualitometers.find(q => q.id === analysis[0]?.qualitometer), [qualitometers, analysis])

    const validAnalysis = useMemo(() => filterValid(filterResult(analysis)), [analysis])
    const maxAnalysis = useMemo(() => searchMaxAnalysis(analysis), [analysis])
    const averageValue = useMemo(() => calculateAverage(analysis, settings), [analysis, settings])

    const nbAnalysis = useMemo(() => analysis.length, [analysis])
    const nbQuantification = useMemo(() => filterQuantification(validAnalysis).length, [validAnalysis])

    const exportAnalysis = useCallback(() => {
        const data = analysis.map(a => ({
            stationCode: qualitometer.code,
            stationName: qualitometer.name,
            date: getDate(a.analysisDate),
            parameterCode: { value: a.parameter, cellType: 'right' },
            parameter: getLabel(parameters, a.parameter),
            unitCode: { value: a.unit, cellType: 'right' },
            unit: getLabel(units, a.unit, 'symbol'),
            result: { value: a.value, color: getMarkerColor(a.color), cellType: 'right' },
            status: getLabel(status, a.status),
            qualification: getLabel(qualifications, a.qualification),
            labo: getLabel(laboratories, a.labo),
            x: { value: qualitometer.x, format: '0.0000000000', cellType: 'number' },
            y: { value: qualitometer.y, format: '0.0000000000', cellType: 'number' },
            projection: { value: qualitometer.projection, format: '0', cellType: 'number' },
        }))
        const dataWithHeaders = data.length ? [
            { ...data[0], headers: ultraLightMode ? EXPORT_ULTRA_LIGHT : keys(data[0]) },
            ...data.slice(1),
        ] : []
        exportFile({
            data: dataWithHeaders,
            exportType: 'xlsx',
            titleFile: `${qualitometer.code}_${qualitometer.name}`,
        })
    }, [qualitometer, qualifications, analysis, units, status, ultraLightMode, laboratories, parameters])

    const headers = useMemo(() => {
        const statusAndResult = { key: 'statusAndResult', value: `${formattedAnalysis.length} ${getI18nTitleDataLength(i18n.result, i18n.results, formattedAnalysis.length)}` }
        if (researchType === RESEARCH_TYPE.INDICES) {
            return ['date', { key: 'parameter', value: i18n.indice }, statusAndResult, 'unit', 'nullValue']
        }
        return ultraLightMode ? ['date', 'parameter', statusAndResult, 'unit', 'nullValue'] : ['labo', 'date', 'parameter', statusAndResult, 'unit', 'nullValue']
    }, [formattedAnalysis.length, researchType, ultraLightMode])

    return (
        <>
            <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
                <AccordionTitle
                    fontColor={blackColor}
                    color={whiteColor}
                    title={(
                        <Grid container columnSpacing={1} rowSpacing={0.5} alignItems='center' sx={{ fontSize: '13px' }}>
                            <Grid size={3}>
                                <Stack>
                                    <span>{qualitometer?.code}</span>
                                    <span>{qualitometer?.name}</span>
                                </Stack>
                            </Grid>
                            <Grid size={2} sx={{ backgroundColor: displayAnalysis.color }}>
                                <Stack>
                                    <span>
                                        {getDate(displayAnalysis.analysisDate)}
                                    </span>
                                    <span>
                                        {displayAnalysis.remark === '7' && i18n.trace || !isUndefined(displayAnalysis.value) && `${displayAnalysis.value} ${unitsIndexed[displayAnalysis.unit]?.symbol ?? ''}` || ''}
                                    </span>
                                </Stack>
                            </Grid>
                            <Grid size={4}>
                                {isOnlyOneParameter && (
                                    <Stack>
                                        <span>
                                            {(showSandreCode ? parameter?.labelWithCode : parameter?.name) ?? ''}
                                        </span>
                                        <span>
                                            {i18n.max}: {`${maxAnalysis?.value ?? ''} ${unitsIndexed[maxAnalysis?.unit]?.symbol ?? ''}`}
                                        </span>
                                        <span>
                                            {i18n.average}: {!isNil(averageValue) && !isNaN(averageValue) ? `${averageValue} ${unitsIndexed[displayAnalysis.unit]?.symbol ?? ''}` : ''}
                                        </span>
                                    </Stack>
                                )}
                                {!isOnlyOneParameter && (
                                    <Grid size={12}>
                                        <span>{`${nbParameter} ${nbParameter < 2 ? i18n.parameter : i18n.parameters}`}</span>
                                    </Grid>
                                )}
                            </Grid>
                            <Grid size={2}>
                                <ProgressBar
                                    progress={nbAnalysis !== 0 ? nbQuantification * 100 / nbAnalysis : 0}
                                    tooltip={ultraLightMode ? i18n.quantificationsNotCalculated : `${nbQuantification} ${i18n.quantificationsRatio} ${nbAnalysis} ${i18n.results}`}
                                />
                            </Grid>
                            <Grid size={1}>
                                <Tooltip title={i18n.accessToTheFile}>
                                    <IconButton>
                                        <Info
                                            sx={{ color: blackColor }}
                                            onClick={e => {
                                                e.stopPropagation()
                                                dispatch(push(`/station/quality/${qualitometer?.id}`))
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={i18n.export}>
                                    <IconButton>
                                        <FileDownload
                                            sx={{ color: blackColor }}
                                            onClick={e => {
                                                e.stopPropagation()
                                                exportAnalysis()
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={i18n.graph}>
                                    <IconButton>
                                        <InsertChart
                                            sx={{ color: blackColor }}
                                            onClick={e => {
                                                e.stopPropagation()
                                                open()
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    )}
                />
                <NewTable
                    rows={formattedAnalysis}
                    headers={headers}

                    rowsPerPageOptions={nbPerPageLabel}
                    onClickRow={({ id }) => openSampleModal(id)}
                    WrapperComponent={WrapperAccordionDetails}
                />
            </Accordion>
            <ParameterGraphModal
                isOpen={isOpen}
                closeGraph={close}
                analysis={analysis}
                thresholds={thresholds}
                hideActions={!isOnlyOneParameter}
            />
        </>
    )
}

CollapsibleTable.propTypes = {
    displayAnalysis: PropTypes.shape({
        // DtoAnalysisLight,
        // calculateThresholdsResult,
    }),
    thresholds: PropTypes.arrayOf(QualityThresholdType),
    analysis: PropTypes.arrayOf(PropTypes.shape({
        // DtoAnalysis ou DtoAnalysisLight
        // calculateThresholdsResult
    })),
    researchType: PropTypes.string,
    isOnlyOneParameter: PropTypes.bool,
    openSampleModal: PropTypes.func,
}

const CartographyAnalysisPanel = ({
    stationsPanelTitle = '',
    thresholds = [],
    analysis = [],
    researchType,
    sortingType,
    fixedAnalysis = [],
}) => {
    const {
        settings,
        qualitometers,
        units,
        qualifications,
    } = useSelector(store => ({
        settings: store.AdministrationReducer.settings,
        qualitometers: store.QualityReducer.qualitometersLight,
        units: store.UnitReducer.units,
        qualifications: store.QualityReducer.qualifications,
    }), shallowEqual)

    const isOnlyOneParameter = useMemo(() => {
        const uniqParam = uniqBy(analysis, 'parameter')
        return uniqParam.length === 1
    }, [analysis])

    const ultraLightMode = useMemo(() => max(analysis.map(a => keys(a).length)) < 20, [analysis])

    const {
        value: isOpen,
        setFalse: close,
        setTrue: open,
    } = useBoolean(false)
    const [selectedAnalysis, setSelectedAnalysis] = useState()

    const [filter, setFilter] = useState({})

    const groupByStations = useMemo(() => {
        const orderedAnalysis = orderBy(analysis, 'analysisDate')
        return groupBy(orderedAnalysis, 'qualitometer')
    }, [analysis])
    const stationsIds = useMemo(() => keys(groupByStations).map(id => parseInt(id)), [groupByStations])

    const filteredStationsIds = useMemo(() => {
        const search = normalizeString(filter.searchValue)
        if (!search) {
            return stationsIds
        }
        return stationsIds.filter(id => {
            const qualitometer = qualitometers.find(q => q.id === id)
            return normalizeString(`${qualitometer?.code ?? ''}${qualitometer?.name ?? ''}`).includes(search)
        })
    }, [filter.searchValue, qualitometers, stationsIds])

    const getDisplayedAnalysis = useCallback(listAnalysis => {
        const validAnalysis = ultraLightMode ? listAnalysis : filterValid(filterResult(listAnalysis))
        if (sortingType === SORTING_TYPE.MAX) {
            return searchMaxAnalysis(validAnalysis)
        } else if (sortingType === SORTING_TYPE.MIN) {
            return searchMinAnalysis(validAnalysis)
        } else if (sortingType === SORTING_TYPE.AVERAGE) {
            const average = calculateAverage(validAnalysis, settings)
            const analyse = validAnalysis[0]
            const averageAnalyse = {
                result: average,
                parameter: analyse?.parameter,
                unit: analyse?.unit,
                fraction: analyse?.fraction,
                support: analyse?.support,
                qualitometer: analyse?.qualitometer,
            }
            return {
                ...averageAnalyse,
                ...calculateThresholdsResult(averageAnalyse, thresholds),
                color: analyse?.color ?? calculateThresholdsResult(averageAnalyse, thresholds).color,
            }
        } else if (sortingType === SORTING_TYPE.P90) {
            return searchP90Analysis(validAnalysis)
        } else if (sortingType === SORTING_TYPE.FIX) {
            const analyse = validAnalysis[0]
            return fixedAnalysis.find(a => a.qualitometer === analyse.qualitometer)
        }
        return maxBy(listAnalysis, 'analysisDate')
    }, [settings, sortingType, ultraLightMode, thresholds, fixedAnalysis])

    const analyse = useMemo(() => {
        return analysis.find(a => a.id === selectedAnalysis)
    }, [analysis, selectedAnalysis])

    const exportStations = useCallback(() => {
        const data = stationsIds.map(id => {
            const qualitometer = qualitometers.find(q => q.id === id)
            const stationAnalysis = groupByStations[id]
            const displayAnalysis = getDisplayedAnalysis(stationAnalysis)

            const validAnalysis = ultraLightMode ? stationAnalysis : filterValid(filterResult(stationAnalysis))

            const p90 = searchP90Value(validAnalysis)
            const min = searchMinValue(validAnalysis)
            const maxValue = searchMaxValue(validAnalysis)
            const average = calculateAverage(validAnalysis, settings)

            return {
                stationCode: qualitometer.code,
                stationName: qualitometer.name,
                cityCode: { value: qualitometer.townCode, cellType: 'right' },
                lastAnalysis: { value: getDate(displayAnalysis?.analysisDate), format: 'dd/MM/yyy', cellType: 'date' },
                result: { value: displayAnalysis?.value, color: displayAnalysis?.color, cellType: 'right' },
                min: { value: min, cellType: 'right' },
                max: { value: maxValue, cellType: 'right' },
                average: { value: average, cellType: 'right' },
                percentile90: { value: p90, cellType: 'right' },
                codeUnit: { value: displayAnalysis?.unit, cellType: 'right' },
                unit: getLabel(units, displayAnalysis?.unit, 'symbol'),
                qualification: getLabel(qualifications, displayAnalysis?.qualification),
                x: { value: qualitometer.x, format: '0.0000000000', cellType: 'number' },
                y: { value: qualitometer.y, format: '0.0000000000', cellType: 'number' },
                projection: { value: qualitometer.projection, format: '0', cellType: 'number' },
            }
        })
        const dataWithHeaders = data.length ? [
            { ...data[0], headers: keys(data[0]) },
            ...data.slice(1),
        ] : []
        exportFile({
            data: dataWithHeaders,
            exportType: 'xlsx',
            titleFile: i18n.research,
        })
    }, [units, getDisplayedAnalysis, ultraLightMode, qualifications, qualitometers, stationsIds, settings, groupByStations])

    const actions = useMemo(() => [{
        iconName: 'file_download',
        tooltip: i18n.export,
        onClick: exportStations,
    }], [exportStations])

    if (analysis.length === 0) {
        return (
            <h5 className='center-align'>{i18n.noAssociatedStations}</h5>
        )
    }

    return (
        <div>
            <Card title={`${stationsPanelTitle} - ${filteredStationsIds.length} ${i18n.stations}`} actions={actions}>
                <Grid container spacing={1} padding={1}>
                    <Grid size={12}>
                        <SimpleFilterField filter={filter} setFilter={setFilter} />
                    </Grid>
                    {filteredStationsIds.map(id => {
                        const stationAnalysis = groupByStations[id]
                        const displayAnalysis = getDisplayedAnalysis(stationAnalysis)
                        return (
                            <Grid size={12} key={id}>
                                <CollapsibleTable
                                    displayAnalysis={displayAnalysis}
                                    analysis={stationAnalysis}
                                    researchType={researchType}
                                    isOnlyOneParameter={isOnlyOneParameter}
                                    thresholds={thresholds}
                                    openSampleModal={analyseId => {
                                        setSelectedAnalysis(analyseId)
                                        open()
                                    }}
                                />
                            </Grid>
                        )
                    })}
                </Grid>
            </Card>
            <SampleModal
                isOpen={isOpen}
                close={close}
                analyse={analyse}
            />
        </div>
    )
}

CartographyAnalysisPanel.propTypes = {
    stationsPanelTitle: PropTypes.string,
    thresholds: PropTypes.arrayOf(QualityThresholdType),
    analysis: PropTypes.arrayOf(PropTypes.shape({
        // DtoAnalysis ou DtoAnalysisLight
        // calculateThresholdsResult
    })),
    researchType: PropTypes.number,
    sortingType: PropTypes.string,
    fixedAnalysis: PropTypes.arrayOf(PropTypes.shape({})),
}

export default CartographyAnalysisPanel